import React from 'react';
import AkIcon from '@atlaskit/icon';
import AttachmentIcon from '@atlaskit/icon/glyph/attachment';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import SearchIcon from '@atlaskit/icon/glyph/editor/search';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import SubtaskIcon from '@atlaskit/icon/glyph/subtask';
import WebLinkIcon from '@atlaskit/icon/glyph/world';
import { ConfluenceIcon } from '@atlaskit/logo';
import {
	ADD_ATTACHMENT_ID,
	CREATE_SUBTASK_ID,
	CREATE_ISSUE_IN_EPIC_ID,
	ADD_ISSUE_LINKS_ID,
	CREATE_CHILD_ID,
	LINK_CONF_PAGE_ID,
	ADD_WEB_LINK_ID,
	LINK_ALERTS_ID,
	INVESTIGATE_INCIDENT_ID,
} from '@atlassian/jira-issue-view-common-constants/src/quick-add-constants';
import { FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { AlertsGlyph } from '@atlassian/jira-servicedesk-incident-management-common';
import { INCIDENTS } from '@atlassian/jira-servicedesk-work-category';
import type { FormatMessage } from '@atlassian/jira-shared-types';
import type {
	QuickAddItemToRender,
	QuickAddItemStateProps,
	QuickAddItemDispatchProps,
} from '../../types';
import messages from '../messages';
import type { IncidentManagementField } from './types';
import { shouldLinkAlertsItemRender } from './utils';

export type ItemListProps = QuickAddItemStateProps &
	QuickAddItemDispatchProps & {
		supportsChildCreation: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		incidentLinking?: Record<any, any> | undefined;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		incidentEscalate?: Record<any, any> | undefined;
		incidentManagement?: IncidentManagementField;
		formatMessage: FormatMessage;
		onLinkAlertsClick?: () => void;
	};

export type ItemList = {
	issueActions: QuickAddItemToRender[];
	linkingActions: QuickAddItemToRender[];
};

const getItemList = (props: ItemListProps): ItemList => {
	const {
		formatMessage,
		canCreateAttachment,
		shouldShowCreateSubtaskButton,
		shouldShowIssueInEpicButton,
		canCreateIssueLink,
		onAddAttachmentClick,
		onCreateSubtaskClick,
		onCreateIssueInEpicClick,
		onChildCreateClick,
		supportsChildCreation,
		isServiceDesk,
		canLinkConfluencePage,
		onLinkConfluencePageClick,
		incidentManagement,
		onLinkAlertsClick,
		canAddWebLinks,
		onAddWebLinkClick,
		onAddIssueLinkClick,
		onInvestigateIncidentClick,
		practices,
	} = props;

	const quickAddActionData: QuickAddItemToRender[] = [
		{
			id: ADD_ATTACHMENT_ID,
			tooltip: formatMessage(messages.addAttachmentTooltip),
			label: formatMessage(messages.addAttachmentLabel),
			icon: <AttachmentIcon size="medium" label="" />,
			onClick: onAddAttachmentClick,
			itemIndex: 0,
			shouldRender: !isServiceDesk && canCreateAttachment,
			testId:
				'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.add-attachment',
		},
		{
			id: CREATE_SUBTASK_ID,
			tooltip: formatMessage(messages.createSubtaskTooltip),
			label: formatMessage(messages.createSubtaskLabel),
			icon: <SubtaskIcon size="medium" label="" />,
			onClick: onCreateSubtaskClick,
			itemIndex: 1,
			shouldRender: shouldShowCreateSubtaskButton,
			testId:
				'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.create-subtask',
		},
		{
			id: CREATE_ISSUE_IN_EPIC_ID,
			tooltip: formatMessage(messages.createChildTooltip),
			label: formatMessage(messages.createChildLabel),
			icon: <ChildIssuesIcon size="medium" label="" />,
			onClick: onCreateIssueInEpicClick,
			itemIndex: 1,
			shouldRender: shouldShowIssueInEpicButton,
			testId:
				'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.create-issue-in-epic',
		},
		{
			id: CREATE_CHILD_ID,
			tooltip: formatMessage(messages.createChildTooltip),
			label: formatMessage(messages.createChildLabel),
			icon: <ChildIssuesIcon size="medium" label="" />,
			onClick: onChildCreateClick,
			itemIndex: 1,
			shouldRender: supportsChildCreation,
			testId:
				'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.create-child-issue',
		},
		{
			id: INVESTIGATE_INCIDENT_ID,
			tooltip: formatMessage(messages.investigateIncidentTooltip),
			label: formatMessage(messages.investigateIncidentLabel),
			icon: (
				<>
					<FireUiAnalytics
						eventName="InvestigateIncidentButton viewed"
						componentName="BentoQuickAddItems"
					/>
					<SearchIcon size="medium" label="" />
				</>
			),
			onClick: onInvestigateIncidentClick,
			itemIndex: 4,
			shouldRender: isServiceDesk && Array.isArray(practices) && practices.includes(INCIDENTS),
			testId:
				'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.investigate-incident',
			hasGlobalSpotlightTarget: true,
		},
	];

	const linkingActionsData: QuickAddItemToRender[] = [
		{
			id: ADD_ISSUE_LINKS_ID,
			tooltip: formatMessage(messages.addIssueLinkTooltip),
			label: formatMessage(messages.addIssueLinkLabel),
			icon: <IssuesIcon size="medium" label="" />,
			onClick: onAddIssueLinkClick,
			itemIndex: 2,
			shouldRender: canCreateIssueLink,
			testId: 'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.link-issue',
		},
		{
			id: LINK_CONF_PAGE_ID,
			tooltip: formatMessage(messages.linkConfluenceContentTooltip),
			label: formatMessage(messages.linkConfluenceContentLabel),
			icon: <ConfluenceIcon appearance="brand" size="small" label="" />,
			onClick: onLinkConfluencePageClick,
			itemIndex: 3,
			shouldRender: canLinkConfluencePage,
			testId: 'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.link-page',
		},
		{
			id: LINK_ALERTS_ID,
			tooltip: formatMessage(messages.linkAlertsTooltip),
			label: formatMessage(messages.linkAlertsLabel),
			// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
			icon: <AkIcon glyph={AlertsGlyph} size="medium" label="" />,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			onClick: onLinkAlertsClick || (() => {}),
			itemIndex: 3,
			shouldRender: shouldLinkAlertsItemRender(incidentManagement),
			testId: 'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.link-alerts',
		},
		{
			id: ADD_WEB_LINK_ID,
			tooltip: formatMessage(messages.addWebLinkTooltip),
			label: formatMessage(messages.addWebLinkLabel),
			icon: <WebLinkIcon size="medium" label="" />,
			onClick: onAddWebLinkClick,
			itemIndex: 3,
			shouldRender: canAddWebLinks,
			testId: 'issue.issue-view.views.issue-base.foundation.quick-add.quick-add-item.link-web',
		},
	];

	return {
		issueActions: quickAddActionData.filter((item) => item.shouldRender),
		linkingActions: linkingActionsData.filter((item) => item.shouldRender),
	};
};

export default getItemList;
